import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    MenuItem,
    TextField,
    Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { getBluedartReplacementCandidates } from '../../store/actions/staffActionCreator';
import {
    ADDITION_TYPES,
    barsRejoining,
    lastWorkingDateOf,
    reasonWithEligibilityOf,
    riderNameOf,
} from '../../pages/approval-workflow/approval.helper';
import RehireIneligibleConfirmDialog from './RehireIneligibleConfirmDialog';

interface ApprovalDecisionModalProps {
    open: boolean;
    row: any;
    canDecide: boolean;
    rehireConfirmationRequired: boolean;
    onClose: () => void;
    onApprove: (
        requestRef: string,
        decision: {
            additionType?: string;
            replacementEmpId?: string;
            remark?: string;
            rehireIneligibleConfirmed?: boolean;
        },
        onDone: () => void,
    ) => void;
    onReject: (requestRef: string, remark: string, onDone: () => void) => void;
}

// The theme's `secondary` is a near-white grey (#e8e8ee), so a secondary text Button renders an
// almost invisible label. Reject is destructive and has to read as such.
const useStyles = makeStyles((theme) => ({
    reject: {
        color: theme.palette.error.main,
        border: `1px solid ${theme.palette.error.main}`,
        '&:hover': {
            backgroundColor: 'rgba(244, 67, 54, 0.08)',
            border: `1px solid ${theme.palette.error.dark}`,
        },
    },
}));

/**
 * Review and decide one request (PRD AP-3, AP-5, and the addition-type step).
 *
 * <p>Approve stays disabled until an addition type is chosen, and a Replacement additionally
 * requires an employee ID picked from the list — free text is refused by the API too, so this is
 * convenience rather than the control.
 *
 * <p>The candidate list is scoped to the request's own hub server-side and deliberately includes
 * deactivated riders: the slot being refilled usually belongs to someone who has already left.
 */
const ApprovalDecisionModal: React.FC<ApprovalDecisionModalProps> = ({
    open,
    row,
    canDecide,
    rehireConfirmationRequired,
    onClose,
    onApprove,
    onReject,
}) => {
    const dispatch = useDispatch();
    const classes = useStyles();

    const [additionType, setAdditionType] = useState('');
    const [replacementEmpId, setReplacementEmpId] = useState('');
    // Held alongside the id because the options list is re-fetched per search term and the picked
    // rider is often not in it — the Autocomplete needs the object to keep showing the selection.
    const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
    const [remark, setRemark] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const [candidateSearch, setCandidateSearch] = useState('');
    const [candidates, setCandidates] = useState<any[]>([]);
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    // The shared useDebounce hook throttles a callback rather than debouncing a value, so the
    // search term is debounced locally — otherwise every keystroke hits the candidates endpoint.
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(
            () => setDebouncedSearch(candidateSearch),
            300,
        );
        return () => clearTimeout(timer);
    }, [candidateSearch]);

    const isOnboarding = row?.requestType === 'ONBOARDING';

    const fetchCandidates = useCallback(() => {
        if (!row?.requestRef || additionType !== 'REPLACEMENT') return;
        setLoadingCandidates(true);
        dispatch(
            getBluedartReplacementCandidates(
                row.requestRef,
                debouncedSearch,
                (response: any) => {
                    setLoadingCandidates(false);
                    setCandidates(response?.response?.candidates ?? []);
                },
                () => {
                    setLoadingCandidates(false);
                    setCandidates([]);
                },
            ),
        );
    }, [dispatch, row, additionType, debouncedSearch]);

    useEffect(() => {
        fetchCandidates();
    }, [fetchCandidates]);

    // The modal stays mounted between requests, so without this a decision made on one request
    // carries into the next — and an employee id from another store is only caught server-side.
    useEffect(() => {
        setAdditionType('');
        setReplacementEmpId('');
        setSelectedCandidate(null);
        setCandidateSearch('');
        setRemark('');
        setCandidates([]);
        setConfirmOpen(false);
    }, [row?.requestRef]);

    // Switching back to New Addition must drop any employee id already picked, or it would be sent
    // alongside — which the API rejects outright.
    useEffect(() => {
        if (additionType !== 'REPLACEMENT') {
            setReplacementEmpId('');
            setSelectedCandidate(null);
            setCandidateSearch('');
        }
    }, [additionType]);

    // The approver is warned in their own right — they did not choose the reason, and the
    // rider's inability to ever come back is on their decision.
    const needsRejoiningConfirmation =
        barsRejoining(row) && rehireConfirmationRequired;

    const doApprove = () => {
        setConfirmOpen(false);
        setSubmitting(true);
        onApprove(
            row.requestRef,
            {
                additionType,
                replacementEmpId,
                remark,
                rehireIneligibleConfirmed: needsRejoiningConfirmation,
            },
            () => setSubmitting(false),
        );
    };

    const approveDisabled =
        submitting ||
        !canDecide ||
        (isOnboarding && !additionType) ||
        (isOnboarding && additionType === 'REPLACEMENT' && !replacementEmpId);

    const detail = (label: string, value?: string | null) => (
        <Box mb={1}>
            <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
                {label}
            </div>
            <div style={{ fontSize: 14, color: '#1f2937' }}>{value || '-'}</div>
        </Box>
    );

    return (
        <>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {row?.requestRef}
                    <Typography variant="body2" color="textSecondary">
                        {row?.requestType} · {row?.status}
                    </Typography>
                </DialogTitle>
                <DialogContent dividers>
                    {!!row?.lastError && (
                        <Box
                            mb={2}
                            p={1}
                            style={{ background: '#fff6f6', borderRadius: 4 }}
                        >
                            <div
                                style={{
                                    color: '#c62828',
                                    fontSize: 12,
                                    fontWeight: 600,
                                }}
                            >
                                Last attempt failed
                            </div>
                            <div style={{ color: '#c62828', fontSize: 12 }}>
                                {row.lastError}
                            </div>
                        </Box>
                    )}

                    <Box display="flex" style={{ gap: 24 }}>
                        <Box flex={1}>
                            {detail('Rider', riderNameOf(row))}
                            {detail('Phone', row?.contactNumber)}
                            {detail('Store', row?.storeId)}
                        </Box>
                        <Box flex={1}>
                            {detail('Zone', row?.zone)}
                            {/* Source is only meaningful for an onboarding; a separation is decided on
                            its reason, which is what the approver needs in its place. */}
                            {isOnboarding
                                ? detail('Source', row?.source)
                                : detail(
                                      'Reason',
                                      reasonWithEligibilityOf(row),
                                  )}
                            {/* Read-only: the date is the initiator's to set, and it is what Blue
                            Dart will record as the resignation date. */}
                            {row?.requestType === 'DEACTIVATION' &&
                                detail(
                                    'Last working day',
                                    lastWorkingDateOf(row),
                                )}
                            {detail(
                                'Raised on',
                                row?.createdAt
                                    ? new Date(row.createdAt).toLocaleString(
                                          'en-IN',
                                      )
                                    : '',
                            )}
                        </Box>
                    </Box>

                    {row?.status !== 'PENDING' && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />
                            {detail(
                                'Decided by',
                                row?.decidedByName
                                    ? `${row.decidedByName} (${row?.decidedByRole ?? ''})`
                                    : row?.decidedByRole,
                            )}
                            {detail(
                                'Decided on',
                                row?.decidedAt
                                    ? new Date(row.decidedAt).toLocaleString(
                                          'en-IN',
                                      )
                                    : '',
                            )}
                            {detail('Remark', row?.decisionRemark)}
                            {row?.additionType &&
                                detail('Addition type', row.additionType)}
                            {row?.replacementEmpId &&
                                detail('Replacing', row.replacementEmpId)}
                        </>
                    )}

                    {row?.status === 'PENDING' && canDecide && (
                        <>
                            <Divider style={{ margin: '12px 0' }} />

                            {isOnboarding && (
                                <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    label="Addition type *"
                                    value={additionType}
                                    onChange={(e) =>
                                        setAdditionType(e.target.value)
                                    }
                                    style={{ marginBottom: 12 }}
                                >
                                    {ADDITION_TYPES.map((option) => (
                                        <MenuItem
                                            key={option.value}
                                            value={option.value}
                                        >
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}

                            {isOnboarding && additionType === 'REPLACEMENT' && (
                                <Autocomplete
                                    options={candidates}
                                    loading={loadingCandidates}
                                    value={selectedCandidate}
                                    // The list is re-fetched per search term, so the selected rider can
                                    // be absent from the current options — without this the field would
                                    // blank itself the moment the term changes.
                                    getOptionSelected={(
                                        option: any,
                                        value: any,
                                    ) => option?.empId === value?.empId}
                                    onChange={(_event, value: any) => {
                                        setSelectedCandidate(value ?? null);
                                        setReplacementEmpId(value?.empId ?? '');
                                    }}
                                    // Only what the user types drives the fetch. Selecting an option
                                    // fires this with reason 'reset' to show the full label, which is
                                    // not a search term and would empty the list behind the selection.
                                    onInputChange={(_event, value, reason) => {
                                        if (reason === 'input') {
                                            setCandidateSearch(value);
                                        } else if (reason === 'clear') {
                                            setCandidateSearch('');
                                        }
                                    }}
                                    getOptionLabel={(option: any) =>
                                        option?.empId
                                            ? `${option.empId} — ${option.name}`
                                            : ''
                                    }
                                    // The server already filters by the search term; filtering again
                                    // locally would hide rows it deliberately returned.
                                    filterOptions={(options: any) => options}
                                    noOptionsText={
                                        loadingCandidates
                                            ? 'Loading…'
                                            : 'No riders with an employee ID at this hub'
                                    }
                                    renderOption={(option: any) => (
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            width="100%"
                                        >
                                            <span>
                                                {option.empId} — {option.name}
                                            </span>
                                            {option.status ===
                                                'DEACTIVATED' && (
                                                <Chip
                                                    size="small"
                                                    label="Deactivated"
                                                    style={{ marginLeft: 8 }}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            label="Replacement Employee ID *"
                                            helperText="Search by employee ID or name — riders at this hub, including deactivated"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loadingCandidates && (
                                                            <CircularProgress
                                                                size={16}
                                                            />
                                                        )}
                                                        {
                                                            params.InputProps
                                                                .endAdornment
                                                        }
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    style={{ marginBottom: 12 }}
                                />
                            )}

                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                size="small"
                                variant="outlined"
                                label="Remark (optional)"
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                            />
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={submitting}>
                        Close
                    </Button>
                    {row?.status === 'PENDING' && canDecide && (
                        <>
                            <Button
                                variant="outlined"
                                className={classes.reject}
                                disabled={submitting}
                                onClick={() => {
                                    setSubmitting(true);
                                    onReject(row.requestRef, remark, () =>
                                        setSubmitting(false),
                                    );
                                }}
                            >
                                Reject
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                disabled={approveDisabled}
                                onClick={() => {
                                    if (needsRejoiningConfirmation) {
                                        setConfirmOpen(true);
                                        return;
                                    }
                                    doApprove();
                                }}
                            >
                                {submitting ? (
                                    <CircularProgress size={18} />
                                ) : (
                                    'Approve & Submit'
                                )}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <RehireIneligibleConfirmDialog
                open={confirmOpen}
                reason={row?.reasonDescription || row?.reasonCode}
                confirmLabel="Confirm and approve"
                onGoBack={() => setConfirmOpen(false)}
                onConfirm={doApprove}
            />
        </>
    );
};

export default ApprovalDecisionModal;
